import { Dispatch, SetStateAction, useMemo, useState } from "react";
import PopupModelWrapper from "../wrappers/PopupModelWrapper";
import { Stack, TextField, Typography } from "@mui/material";
import PopupButton from "../common/PopupButton";
import { domainsInputBox, domainsInputPlaceholder } from "./modelStyles";
import { Order } from "./BackorderModel";
import { validators } from "src/data/validators";
import useMessage from "src/hooks/useMessage";

type BackorderInput = {
    open: boolean;
    availBidsCount: number;
    setOpen: Dispatch<SetStateAction<boolean>>;
    setIsPriorityOpen: Dispatch<SetStateAction<boolean>>;
    setDomains: React.Dispatch<React.SetStateAction<Order[]>>;
};

const BackorderInputModel = (props: BackorderInput) => {
    const { open, availBidsCount, setOpen, setIsPriorityOpen, setDomains } =
        props;

    const { showError } = useMessage();

    const [domainsInput, setDomainsInput] = useState<string>("");
    const [error, setError] = useState<string>("");

    const domainsCount = useMemo(() => {
        return domainsInput.trim() ? domainsInput.split("\n").length : 0;
    }, [domainsInput]);

    const handleClose = () => {
        setError("");
        setDomainsInput("");
        setOpen(false);
    };

    const moveToPrioritySelection = () => {
        const processedDomains = [];
        const processedDomainNames: string[] = [];

        const inputLines = domainsInput.split("\n");

        for (const line of inputLines) {
            const cleanLine = line
                .replace(/\u00A0/g, "")
                .replace(/[\s]+/g, "")
                .trim();

            const lineArray = cleanLine.split(",");

            if (lineArray.length === 0) {
                continue;
            }

            const domainName = lineArray[0].trim();

            const isValidDomainName = validators.DOMAIN_NAME.test(domainName);

            if (!isValidDomainName) {
                console.log("Invalid Domain Name Removed from Backorder");
                continue;
            }

            if (processedDomainNames.includes(domainName)) {
                console.log("Duplicate Domain Name Removed from Backorder");
                continue;
            }

            const priority = Math.floor(Number(lineArray[1] || "1") || 1);
            const cappedPriority = Math.min(5, Math.max(1, priority));

            processedDomainNames.push(domainName);
            processedDomains.push({
                domainName: domainName,
                priority: cappedPriority,
            });
        }

        if (processedDomains.length === 0) {
            setError("No Valid Domains, Check the Input !");
            return;
        }

        if (domainsCount > processedDomains.length) {
            showError("Some invalid backorders were removed");
        }

        setDomains(processedDomains);
        setIsPriorityOpen(true);
        handleClose();
    };

    const handleDomainInput = (e: any) => {
        const value = e.target.value.toLowerCase();
        setDomainsInput(value);

        if (error.length > 0) {
            setError("");
        }
    };

    return (
        <PopupModelWrapper
            open={open}
            handleClose={handleClose}
            title="Add Domains"
        >
            <Stack>
                <Typography align={"right"} marginTop={2}>
                    {domainsCount + "/" + availBidsCount}
                </Typography>
                <TextField
                    id="domains-input-text-field"
                    variant="outlined"
                    multiline
                    hiddenLabel
                    placeholder={domainsInputPlaceholder}
                    sx={domainsInputBox}
                    rows={4}
                    value={domainsInput}
                    onChange={handleDomainInput}
                    error={error !== ""}
                    helperText={error !== "" && error}
                />
                <Stack direction="row" justifyContent="flex-end">
                    <PopupButton
                        triggerFunction={handleClose}
                        type="cancel"
                        id="domains-input-popup-cancel"
                    >
                        Cancel
                    </PopupButton>
                    <PopupButton
                        triggerFunction={moveToPrioritySelection}
                        type="proceed"
                        id="domains-input-popup-add"
                        disabled={error !== ""}
                    >
                        Add
                    </PopupButton>
                </Stack>
            </Stack>
        </PopupModelWrapper>
    );
};

export default BackorderInputModel;
